// Filtrar por un campo que a veces no tiene valor.
//
// POR QUÉ EXISTE
// --------------
// Un desplegable que sólo ofrece los valores que existen esconde las filas que
// no tienen ninguno, y lo hace en silencio: el usuario filtra «NAC», no le
// salen, y da por hecho que no hay. Medido en cargas de salida: 12.665 de
// 57.892 no tienen grupo de cliente, y 15.387 no tienen incoterm.
//
// La solución es ofrecer el hueco como una opción más. Eso exige un valor que
// represente «no tiene valor» y que no pueda confundirse con uno real, porque
// viaja por la URL junto a los demás.
import type { OpcionMulti } from '@/components/common/MultiFiltro'

/** El valor que significa «esta fila no tiene dato en este campo».
 *
 *  Los guiones bajos dobles no aparecen en ningún código de grupo ni de
 *  incoterm del ERP, así que no puede chocar con un valor de verdad. Va en la
 *  URL tal cual, que es feo pero honesto: se ve qué se está filtrando. */
export const SIN_INFORMAR = '__sin__'

/** ¿Esta fila entra en la selección?
 *
 *  Selección vacía = no filtra nada, que es lo que espera cualquiera que no
 *  haya tocado el control.
 *
 *  El blanco y el nulo cuentan igual: el backend ya los unifica, pero esta
 *  función también, para que no dependa de que el de enfrente lo haga bien. */
export function casaConHueco(
  valor: string | null | undefined,
  seleccion: string[],
): boolean {
  if (seleccion.length === 0) return true
  const limpio = (valor ?? '').trim()
  if (!limpio) return seleccion.includes(SIN_INFORMAR)
  return seleccion.includes(limpio)
}

/** El catálogo del desplegable: los valores que hay, más el hueco si lo hay.
 *
 *  El hueco sólo se ofrece cuando alguna fila lo tiene. Ofrecerlo siempre daría
 *  una opción que no selecciona nada, que es otra forma de mentir. Va el
 *  ÚLTIMO: es una categoría de otro orden que los valores reales. */
export function opcionesConHueco(
  valores: (string | null | undefined)[],
  etiquetaHueco: string,
): OpcionMulti[] {
  const reales = new Set<string>()
  let hayHueco = false
  for (const v of valores) {
    const limpio = (v ?? '').trim()
    if (limpio) reales.add(limpio)
    else hayHueco = true
  }
  const opciones: OpcionMulti[] = [...reales]
    .sort((a, b) => a.localeCompare(b))
    .map(valor => ({ valor, titulo: valor }))
  if (hayHueco) opciones.push({ valor: SIN_INFORMAR, titulo: etiquetaHueco })
  return opciones
}

/** Cómo se lee un valor de este filtro en un chip o en una celda. */
export function etiquetaHuecoDe(etiquetaHueco: string) {
  return (valor: string) => (valor === SIN_INFORMAR ? etiquetaHueco : valor)
}
